import { useUser } from "@/hooks/auth/use-user";
import { Loading, Typography } from "@backpac/monica";
import { Suspense } from "react";
import { Link, Outlet } from "react-router";

/**
 * Layout for /admin/support/*. Deliberately does NOT use
 * `AppWorkspaceLayout` (no `:workspaceId` in the URL, no workspace-scoped
 * data needed) and does NOT modify `AuthBoundary`.
 *
 * Auth gate: `useUser()` is called un-wrapped by any local error boundary
 * (matching the existing `WorkspaceNavigator` pattern in
 * `routes/workspace-navigator.tsx`), so an unauthenticated visitor's
 * `KnownAuthError` bubbles up to the app-level `<AuthBoundary>` in
 * `App.tsx`, which redirects to sign-in with a `redirect_url` back here.
 * That satisfies "authenticated, no workspace requirement" without touching
 * `AuthBoundary` itself.
 *
 * This only confirms the visitor has *a* logged-in Supabase session. Whether
 * they're actually support staff is enforced server-side (see the PRD's
 * "server enforces" note) — a non-staff logged-in user will get a 403 from
 * the admin support endpoints, which the list/detail pages handle and
 * display as a plain "not authorized" state.
 */
export function SupportAdminLayout() {
  return (
    <div className="min-h-screen w-full bg-backgroundLight">
      <header className="border-b border-borderLight bg-white px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
        <Link to="/admin/support" className="inline-flex items-center gap-2">
          <Typography asChild size="lg" font="unbounded" color="textPrimary">
            <span>BackPac Support Inbox</span>
          </Typography>
        </Link>
        <nav className="flex items-center gap-4">
          <Link to="/admin/support" className="underline-offset-4 hover:underline">
            <Typography asChild size="sm" color="textSecondary">
              <span>문의함</span>
            </Typography>
          </Link>
          <Link to="/admin/support/followups" className="underline-offset-4 hover:underline">
            <Typography asChild size="sm" weight={600} color="textPrimary">
              <span>대응 목록</span>
            </Typography>
          </Link>
        </nav>
      </header>
      <main className="px-6 py-8">
        <Suspense fallback={<Loading.FullHeight />}>
          <AdminAuthGate />
        </Suspense>
      </main>
    </div>
  );
}

function AdminAuthGate() {
  // Throws KnownAuthError (caught by the top-level AuthBoundary) if there is
  // no logged-in session at all.
  useUser();
  return <Outlet />;
}
